"use client";

import React, { useEffect, useState } from "react";
import styles from "./ExploreStateCard.module.scss";
import { MyProfileStateCardData } from "@components/molecules/MyProfileStateCard/MyProfileStateCard.data";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import assetLoader from "@utils/internalImageLoder";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { formatCurrencyINR } from "@utils/numberFormatter";
import { useAppDispatch } from "@lib/redux/hooks";
import { setCopilotCarData } from "@lib/redux/slices/carDataSlice";
import { PAGE_TYPES } from "@constants/pageTypes";
import { useEqualHeights } from "@hooks/useEqualHeights";
interface ExploreStateCardProps {
  card: MyProfileStateCardData;
  cityName?: string;
  isLoggedIn?: any;
  balance?: any;
}

const ExploreStateCard: React.FC<ExploreStateCardProps> = ({
  card,
  cityName,
  isLoggedIn,
  balance,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const { showLoader, hideLoader } = useGlobalLoader();
  const dispatch = useAppDispatch();
  
  const { elementRef: mainInfoRef } = useEqualHeights({ 
    sectionType: 'mainInfo',
    dependencies: [isLoggedIn, balance],
  });
  const { elementRef: cardFooterRef } = useEqualHeights({ 
    sectionType: 'cardFooter',
    dependencies: [isLoggedIn, balance],
  });

  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const handleAskSid = async (prompt: string) => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_chat_bot",
        prompt: prompt || "",
        city_name: cityName,
      },
    });

    dispatch(setCopilotCarData({
      make: "",
      model: "",
      variant: "",
      color: "",
    }));

    (window as any).copilot.open();
    (window as any).copilot.sendMessage("Hello");
  };

  const handleClick = async () => {
    showLoader("Loading...");
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: PAGE_TYPES.HOME_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_buy_car",
        city_name: cityName,
      },
    });
    router.push("/cars/");
  };

  return (
    <>
      <div className={styles.cardContainer}>
        <div className={styles.cardContent}>
          <div className={styles.mainInfo} ref={mainInfoRef} data-section-type="mainInfo">
            <div className={styles.IconWitharrow}>
              <div className={styles.innerTitleWithIcon}>
                <Image
                  src={`${assetLoader()}${card.icon}`}
                  alt='Arrow Right'
                  fill
                  className={styles.ctaSVG}
                  priority
                />
                <Link
                  href="/cars/"
                  onClick={handleClick}
                  className={styles.ctaButton}
                >
                  <Image
                    src={`${assetLoader()}/icons/Arrow_right.svg`}
                    alt={card.ctaIconName}
                    fill
                    className={styles.ctaIcon}
                    priority
                  />
                </Link>
              </div>
                <Link href="/cars/" onClick={handleClick}>
                  <h3 className={styles.title}>{card.title}</h3>
                </Link>
            </div>
            {isLoggedIn ? (
              balance > 0 ? (
                <p className={styles.subText}>
                  {card.subText.map((segment, segmentIndex) => (
                    <span
                      key={segmentIndex}
                      className={segment.color ? `${styles[segment.color]} ${styles.subTextColored}` : ""}
                    >
                      {!segment.text
                        ? `${formatCurrencyINR(balance)} unlocked`
                        : segment.text}
                    </span>
                  ))}
                </p>
              ) 
              : (
              <p className={styles.subText}>
                {card.subTextNoBalance.map((segment: any, segmentIndex: any) => (
                  <span
                    key={segmentIndex}
                    className={segment.color ? `${styles[segment.color]} ${styles.subTextColored}` : ""}
                  >
                    {!segment.text
                      ? `${formatCurrencyINR(balance)} unlocked`
                      : segment.text}
                  </span>
                ))}
              </p>
              )
            ) : (
              <p className={styles.subText}>
                Check express delivery and ACKO Drive discounts inside
              </p>
            )}
          </div>
          <div className={styles.cardFooter} ref={cardFooterRef} data-section-type="cardFooter">
              <div className={styles.divider}></div>
              <div className={styles.exploreAdditionalCtaContainer}>
                {card.introText && (
                  <p className={styles.introText}>
                    <span>{card.introTextBold}</span> {card.introText}
                  </p>
                )}
                {card.additionalCtaText && (
                  <button
                    onClick={() => handleAskSid("Hello")}
                    className={styles.additionalCtaButton}
                  >
                    {card.additionalCtaText}
                  </button>
                )}
              </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreStateCard;
